/**
 * ShiftPlanPage — DRR-211 Shift Plan Visualization
 *
 * Real-time view of the coordination engine's optimized task assignments.
 * Shows current shift plan with staff assignments, task priorities, and
 * resource reservations.
 *
 * Route: /ferp-core/coordination/shift-plan
 * Nav group: admin (DRR-196)
 * Scope: coordination:view (DRR-102)
 * Data tier: T2 operational (DRR-114)
 *
 * Key features:
 * - Current shift plan with task-to-staff assignments
 * - Replan triggers (callout, emergency, incident)
 * - Weight configuration display (floors + learnable)
 * - Plan adherence tracking (planned vs actual)
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  Menu,
  Paper,
  Progress,
  RingProgress,
  ScrollArea,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  ThemeIcon,
  Timeline,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconRefresh,
  IconSettings,
  IconUser,
  IconUsers,
  IconCalendarEvent,
  IconScale,
  IconBath,
  IconPill,
  IconTarget,
  IconTrendingUp,
} from '@tabler/icons-react';

import { useAuth, useTenantId } from '@/auth';
import {
  useCoordinationWeights,
  useActiveShiftPlan,
  useShiftPlanning,
  useFacilityFloors,
  REPLAN_TRIGGER_LABELS,
  ReplanTriggerType,
} from '@/hooks/useCoordination';
import type {
  GetActiveShiftPlanQuery,
  GetCoordinationWeightsQuery,
} from '@/generated/graphql';

type GqlShiftPlan = NonNullable<GetActiveShiftPlanQuery['activeShiftPlan']>;
type GqlTaskAssignment = NonNullable<GqlShiftPlan['assignments']>[number];
type GqlCoordinationWeights = NonNullable<GetCoordinationWeightsQuery['coordinationWeights']>;

// ---------------------------------------------------------------------------
// Floor selector (for multi-floor facilities)
// ---------------------------------------------------------------------------

function FloorSelector({
  value,
  onChange,
  floors,
  loading,
}: {
  value: string;
  onChange: (v: string) => void;
  floors: Array<{ value: string; label: string }>;
  loading: boolean;
}) {
  return (
    <Select
      label="Floor"
      data={floors}
      value={value}
      onChange={(v) => v && onChange(v)}
      w={200}
      placeholder={loading ? 'Loading floors...' : 'Select floor'}
      disabled={loading || floors.length === 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Shift Stats Cards
// ---------------------------------------------------------------------------

interface ShiftStatsProps {
  plan: GqlShiftPlan | null;
  loading: boolean;
}

function ShiftStats({ plan, loading }: ShiftStatsProps) {
  if (loading) {
    return (
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={100} radius="md" />
        ))}
      </SimpleGrid>
    );
  }

  if (!plan) {
    return (
      <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
        No active shift plan. Click "Plan Shift" to generate one.
      </Alert>
    );
  }

  const totalTasks = plan.assignments?.length ?? 0;
  // Note: status field not in schema yet - tracking planned vs actual is Phase 2
  const staffCount = plan.staffRoster?.filter((s) => s.available)?.length ?? 0;

  const stats = [
    {
      label: 'Total Tasks',
      value: totalTasks,
      icon: IconCalendarEvent,
      color: 'blue',
    },
    {
      label: 'Assigned',
      value: totalTasks,
      icon: IconCheck,
      color: 'green',
    },
    {
      label: 'Staff on Shift',
      value: staffCount,
      icon: IconUsers,
      color: 'violet',
    },
    {
      label: 'Plan Version',
      value: plan.version ?? 1,
      icon: IconTarget,
      color: 'blue',
    },
  ];

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      {stats.map((stat) => (
        <Card key={stat.label} withBorder padding="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {stat.label}
              </Text>
              <Text size="xl" fw={700}>
                {stat.value}
              </Text>
            </div>
            <ThemeIcon color={stat.color} variant="light" size="xl" radius="md">
              <stat.icon size={24} />
            </ThemeIcon>
          </Group>
        </Card>
      ))}
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------------------
// Staff Assignment Grid
// ---------------------------------------------------------------------------

interface StaffAssignmentGridProps {
  assignments: GqlTaskAssignment[];
  loading: boolean;
}

function StaffAssignmentGrid({ assignments, loading }: StaffAssignmentGridProps) {
  // Group by staff
  const byStaff = useMemo(() => {
    const map = new Map<string, GqlTaskAssignment[]>();
    for (const a of assignments) {
      const staffId = a.assignedStaffId ?? 'unassigned';
      const list = map.get(staffId) ?? [];
      list.push(a);
      map.set(staffId, list);
    }
    return map;
  }, [assignments]);

  if (loading) {
    return (
      <Grid>
        {[1, 2, 3].map((i) => (
          <Grid.Col key={i} span={{ base: 12, md: 4 }}>
            <Skeleton height={200} radius="md" />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  if (assignments.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No assignments in current plan.
      </Text>
    );
  }

  return (
    <Grid>
      {Array.from(byStaff.entries()).map(([staffId, tasks]) => {
        const total = tasks.length;
        // Priority score as a proxy for assignment quality
        const avgPriority = tasks.reduce((sum, t) => sum + (t.priorityScore ?? 0), 0) / total;

        return (
          <Grid.Col key={staffId} span={{ base: 12, md: 6, lg: 4 }}>
            <Card withBorder padding="md">
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <ThemeIcon color="blue" variant="light" size="md">
                    <IconUser size={16} />
                  </ThemeIcon>
                  <Text fw={600}>{staffId}</Text>
                </Group>
                <Badge color="blue">
                  {total} tasks
                </Badge>
              </Group>

              <Group gap="xs" mb="md">
                <Text size="xs" c="dimmed">Avg Priority:</Text>
                <Badge size="xs" color={avgPriority > 70 ? 'red' : avgPriority > 40 ? 'yellow' : 'green'}>
                  {avgPriority.toFixed(0)}
                </Badge>
              </Group>

              <ScrollArea h={150}>
                <Stack gap="xs">
                  {tasks.map((task) => (
                    <Group
                      key={task.taskId}
                      justify="space-between"
                      p="xs"
                      style={{
                        backgroundColor: 'var(--mantine-color-gray-0)',
                        borderRadius: 'var(--mantine-radius-sm)',
                      }}
                    >
                      <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
                        Task {task.taskId.slice(-6)}
                      </Text>
                      <Group gap={4}>
                        <Badge size="xs" color="gray">
                          P{task.priorityScore ?? 0}
                        </Badge>
                        {task.assistStaffId && (
                          <Badge size="xs" color="violet">
                            2-person
                          </Badge>
                        )}
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </ScrollArea>
            </Card>
          </Grid.Col>
        );
      })}
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Weights Display
// ---------------------------------------------------------------------------

interface WeightsDisplayProps {
  weights: GqlCoordinationWeights | null;
  loading: boolean;
}

function WeightsDisplay({ weights, loading }: WeightsDisplayProps) {
  if (loading) {
    return <Skeleton height={200} radius="md" />;
  }

  if (!weights) {
    return (
      <Alert color="yellow">
        Coordination weights not loaded.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Regulatory Floors */}
      <Card withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon color="red" variant="light">
            <IconScale size={16} />
          </ThemeIcon>
          <Text fw={600}>Regulatory Floors (Immutable)</Text>
        </Group>

        <SimpleGrid cols={2}>
          <Text size="sm">
            <Text span c="dimmed">ULP Ratio (Day):</Text>{' '}
            1:{weights.staffingFloors?.ulpRatioDay ?? 20}
          </Text>
          <Text size="sm">
            <Text span c="dimmed">ULP Ratio (Night):</Text>{' '}
            1:{weights.staffingFloors?.ulpRatioNight ?? 25}
          </Text>
          <Text size="sm">
            <Text span c="dimmed">Memory Care:</Text>{' '}
            1:{weights.staffingFloors?.ulpRatioMemoryCare ?? 10}
          </Text>
          <Text size="sm">
            <Text span c="dimmed">Min Awake Staff:</Text>{' '}
            {weights.staffingFloors?.minimumAwakeStaff ?? 1}
          </Text>
        </SimpleGrid>
      </Card>

      {/* Learnable Weights */}
      <Card withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon color="blue" variant="light">
            <IconTrendingUp size={16} />
          </ThemeIcon>
          <Text fw={600}>Task Priority Weights (Learnable)</Text>
        </Group>

        <Stack gap="xs">
          {[
            { label: 'Acuity', value: weights.taskPriorityWeights?.acuityWeight ?? 25 },
            { label: 'Deadline', value: weights.taskPriorityWeights?.deadlineWeight ?? 25 },
            { label: 'Continuity', value: weights.taskPriorityWeights?.continuityWeight ?? 25 },
            { label: 'Resource', value: weights.taskPriorityWeights?.resourceWeight ?? 15 },
            { label: 'Sequence', value: weights.taskPriorityWeights?.sequenceWeight ?? 10 },
          ].map((w) => (
            <Group key={w.label} justify="space-between">
              <Text size="sm">{w.label}</Text>
              <Group gap="xs">
                <Progress value={w.value} size="sm" w={100} />
                <Text size="xs" c="dimmed" w={30}>
                  {w.value}%
                </Text>
              </Group>
            </Group>
          ))}
        </Stack>
      </Card>

      {/* Feature Flags */}
      <Card withBorder>
        <Group gap="xs" mb="md">
          <ThemeIcon color="grape" variant="light">
            <IconSettings size={16} />
          </ThemeIcon>
          <Text fw={600}>Features</Text>
        </Group>

        <Group gap="md">
          <Badge
            color={weights.cognitiveContinuityEnabled ? 'green' : 'gray'}
            variant="dot"
          >
            Cognitive Continuity
          </Badge>
          <Badge
            color={weights.twoPersonAssistEnabled ? 'green' : 'gray'}
            variant="dot"
          >
            Two-Person Assist
          </Badge>
          <Text size="sm" c="dimmed">
            Target: {weights.targetContinuityPercent ?? 80}% continuity
          </Text>
        </Group>
      </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Replan Menu
// ---------------------------------------------------------------------------

interface ReplanMenuProps {
  planId: string;
  onReplan: (trigger: ReplanTriggerType) => void;
  loading: boolean;
}

function ReplanMenu({ planId, onReplan, loading }: ReplanMenuProps) {
  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button
          variant="light"
          color="orange"
          leftSection={<IconRefresh size={16} />}
          rightSection={<IconChevronDown size={14} />}
          loading={loading}
          disabled={!planId}
        >
          Replan
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Replan Trigger</Menu.Label>
        {Object.entries(REPLAN_TRIGGER_LABELS).map(([key, label]) => (
          <Menu.Item
            key={key}
            onClick={() => onReplan(key as ReplanTriggerType)}
          >
            {label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function ShiftPlanPage() {
  const tenantId = useTenantId() ?? '';
  const [floorId, setFloorId] = useState('');

  const { floors, loading: floorsLoading, hasFloors } = useFacilityFloors();
  const { weights, loading: weightsLoading } = useCoordinationWeights();
  const { plan, loading: planLoading, refetch } = useActiveShiftPlan(tenantId, floorId);
  const { planShift, replanShift, loading: mutationLoading } = useShiftPlanning();

  // Auto-select first floor when floors load
  React.useEffect(() => {
    const firstFloor = floors[0];
    if (firstFloor && !floorId) {
      setFloorId(firstFloor.value);
    }
  }, [floors, floorId]);

  const handlePlanShift = useCallback(async () => {
    if (!tenantId) return;
    await planShift(tenantId, floorId);
    refetch();
  }, [tenantId, floorId, planShift, refetch]);

  const handleReplan = useCallback(async (trigger: ReplanTriggerType) => {
    if (!tenantId || !plan?.planId) return;
    await replanShift(tenantId, plan.planId, trigger);
    refetch();
  }, [tenantId, plan?.planId, replanShift, refetch]);

  const loading = weightsLoading || planLoading || floorsLoading;

  return (
    <Stack gap="lg" p="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Shift Plan</Title>
          <Text c="dimmed" size="sm">
            DRR-211 Care Coordination — Optimized task assignments
          </Text>
        </div>

        <Group>
          <FloorSelector
            value={floorId}
            onChange={setFloorId}
            floors={floors}
            loading={floorsLoading}
          />

          <Button
            leftSection={<IconCalendarEvent size={16} />}
            onClick={handlePlanShift}
            loading={mutationLoading}
          >
            Plan Shift
          </Button>

          <ReplanMenu
            planId={plan?.planId ?? ''}
            onReplan={handleReplan}
            loading={mutationLoading}
          />
        </Group>
      </Group>

      {/* No floors warning */}
      {!floorsLoading && !hasFloors && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
          No facility zones configured. Zone data is loaded from{' '}
          <Text span ff="monospace" size="sm">zone-definitions.yaml</Text>.
          Run the seeder to populate zone data.
        </Alert>
      )}

      {/* Stats */}
      <ShiftStats plan={plan} loading={loading} />

      <Divider />

      {/* Main content tabs */}
      <Tabs defaultValue="assignments">
        <Tabs.List>
          <Tabs.Tab value="assignments" leftSection={<IconUsers size={14} />}>
            Staff Assignments
          </Tabs.Tab>
          <Tabs.Tab value="weights" leftSection={<IconScale size={14} />}>
            Weights Configuration
          </Tabs.Tab>
          <Tabs.Tab value="timeline" leftSection={<IconClock size={14} />}>
            Shift Timeline
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="assignments" pt="md">
          <StaffAssignmentGrid
            assignments={plan?.assignments ?? []}
            loading={loading}
          />
        </Tabs.Panel>

        <Tabs.Panel value="weights" pt="md">
          <WeightsDisplay weights={weights} loading={weightsLoading} />
        </Tabs.Panel>

        <Tabs.Panel value="timeline" pt="md">
          <Card withBorder p="xl">
            <Stack align="center" gap="md">
              <ThemeIcon size="xl" color="gray" variant="light">
                <IconClock size={32} />
              </ThemeIcon>
              <Text c="dimmed">
                Timeline view coming in DRR-222 (Temporal Heatmap)
              </Text>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
